import React, { useRef, useState } from 'react'

const DEMO_LISTS = [
  { id: 7201, name: 'Subscribers - no youth programming attendance', category: 'Subscriptions', size: 14 },
  { id: 7202, name: 'Single ticket buyers - family matinee prospects', category: 'Acquisition', size: 18 },
  { id: 7203, name: 'Annual fund donors - household profile refresh', category: 'Annual Fund', size: 12 },
  { id: 7204, name: 'Lapsed patrons - education campaign', category: 'Reactivation', size: 16 },
  { id: 7205, name: 'Opening weekend buyers - audience insights', category: 'Events', size: 10 },
]

const CATEGORIES = ['All categories', ...Array.from(new Set(DEMO_LISTS.map(l => l.category))).sort()]
const NAMES = ['Abbott, Claire L.', 'Chen, Wei', 'Fletcher, James R.', 'Harrison, Nathaniel', 'Patel, Priya S.', "O'Brien, Kathleen", 'Nguyen, Bao T.', 'Ramirez, Diana', 'Lawson, Marcus D.', 'Vega, Sofia']
const INDUSTRIES = ['Healthcare', 'Education', 'Technology', 'Finance', 'Hospitality', 'Legal Services', 'Manufacturing', 'Real Estate', 'Nonprofit', 'Government']
const INCOME_BANDS = ['$45K-$65K', '$65K-$85K', '$85K-$110K', '$110K-$150K', '$150K-$200K', '$200K+']

function profileFor(custNo) {
  const n = Number(custNo) || 0
  return {
    custNo,
    name: NAMES[n % NAMES.length],
    income: INCOME_BANDS[n % INCOME_BANDS.length],
    industry: INDUSTRIES[n % INDUSTRIES.length],
    kids: n % 3 === 0 || n % 5 === 0 ? 'Yes' : 'No',
    confidence: n % 4 === 0 ? 'High' : n % 4 === 1 ? 'Medium' : 'Modeled',
  }
}

function buildRows(listId) {
  const list = DEMO_LISTS.find(l => l.id === Number(listId))
  return Array.from({ length: list?.size || 10 }, (_, i) => profileFor(Number(listId) * 100 + i + 1))
}

function YesNoPill({ value }) {
  const yes = value === 'Yes'
  return <span style={{ ...s.pill, color: yes ? '#16a34a' : '#6b7280', background: yes ? '#dcfce7' : '#f3f4f6' }}>{value}</span>
}

function StatPill({ label, value, color }) {
  return (
    <div style={{ ...s.statPill, borderColor: color }}>
      <div style={{ ...s.statValue, color }}>{value}</div>
      <div style={{ ...s.statLabel, color }}>{label}</div>
    </div>
  )
}

function BulkScreeningTab() {
  const [search, setSearch] = useState('')
  const [category, setCategory] = useState('All categories')
  const [selectedList, setSelectedList] = useState('')
  const [stage, setStage] = useState('idle')
  const [previewRows, setPreviewRows] = useState([])
  const [processed, setProcessed] = useState(0)
  const [results, setResults] = useState([])
  const cancelRef = useRef(false)

  const filteredLists = DEMO_LISTS.filter(l => {
    const categoryMatch = category === 'All categories' || l.category === category
    const searchMatch = !search.trim() || l.name.toLowerCase().includes(search.trim().toLowerCase())
    return categoryMatch && searchMatch
  })
  const selectedListObj = DEMO_LISTS.find(l => String(l.id) === String(selectedList))

  const handlePreview = () => {
    const rows = buildRows(selectedList)
    setPreviewRows(rows)
    setResults([])
    setProcessed(0)
    cancelRef.current = false
    setStage('previewing')
  }

  const handleRun = async () => {
    setStage('running')
    setResults([])
    setProcessed(0)
    const done = []
    for (let i = 0; i < previewRows.length; i++) {
      if (cancelRef.current) break
      await new Promise(r => setTimeout(r, 120))
      done.push(previewRows[i])
      setResults([...done])
      setProcessed(i + 1)
    }
    setStage('done')
  }

  const reset = () => {
    setStage('idle')
    setPreviewRows([])
    setResults([])
    setProcessed(0)
    cancelRef.current = false
  }

  const kidsYes = results.filter(r => r.kids === 'Yes').length
  const highIncome = results.filter(r => ['$150K-$200K', '$200K+'].includes(r.income)).length
  const topIndustries = Object.entries(results.reduce((acc, row) => {
    acc[row.industry] = (acc[row.industry] || 0) + 1
    return acc
  }, {})).sort((a, b) => b[1] - a[1]).slice(0, 3)

  return (
    <div>
      {stage === 'idle' && (
        <>
          <div style={s.panelIntro}>
            <h2 style={s.sectionHeading}>Screen a Tessitura list</h2>
            <p style={s.sectionBody}>Choose a list, preview the constituents, then run demographic screening.</p>
          </div>
          <div style={s.formGrid}>
            <div style={s.fieldGroup}>
              <label style={s.fieldLabel}>Search by list name</label>
              <input value={search} onChange={e => { setSearch(e.target.value); setSelectedList('') }} placeholder="Search all Tessitura lists..." style={s.input} />
            </div>
            <div style={s.fieldGroup}>
              <label style={s.fieldLabel}>List category</label>
              <select value={category} onChange={e => { setCategory(e.target.value); setSelectedList('') }} style={s.select}>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div style={s.fieldGroup}>
              <label style={s.fieldLabel}>Tessitura list</label>
              <select value={selectedList} onChange={e => setSelectedList(e.target.value)} style={s.select}>
                <option value="">{filteredLists.length === 0 ? 'No lists found' : 'Choose a list'}</option>
                {filteredLists.map(l => <option key={l.id} value={l.id}>{l.name} (#{l.id})</option>)}
              </select>
            </div>
            <button onClick={handlePreview} disabled={!selectedList} style={{ ...s.btn, ...s.formAction, opacity: !selectedList ? 0.5 : 1 }}>Start screening</button>
          </div>
        </>
      )}

      {stage === 'previewing' && (
        <div style={s.previewBox}>
          <h3 style={s.sectionHeading}>Ready to screen {previewRows.length} constituents</h3>
          <p style={s.sectionBody}>Tess Buddy will append demographic estimates to every constituent in <strong>{selectedListObj?.name}</strong>.</p>
          <div style={s.buttonRow}>
            <button onClick={handleRun} style={s.btn}>Run Demographic Screening</button>
            <button onClick={reset} style={s.secondaryBtn}>Back</button>
          </div>
        </div>
      )}

      {stage === 'running' && (
        <div style={s.previewBox}>
          <h3 style={s.sectionHeading}>Screening {processed} of {previewRows.length}...</h3>
          <p style={s.sectionBody}>{processed < previewRows.length ? 'Matching household and business signals...' : 'Wrapping up...'}</p>
          <div style={s.progressBar}><div style={{ ...s.progressFill, width: `${previewRows.length ? Math.round((processed / previewRows.length) * 100) : 0}%` }} /></div>
          <div style={s.buttonRow}><button onClick={() => { cancelRef.current = true }} style={s.secondaryBtn}>Stop</button></div>
        </div>
      )}

      {stage === 'done' && results.length > 0 && (
        <>
          <div style={s.previewBox}>
            <div style={s.summaryHeader}>
              <div>
                <h3 style={{ ...s.sectionHeading, margin: 0 }}>Screening complete</h3>
                <p style={s.summarySub}>{selectedListObj?.name} · {results.length} constituents screened</p>
              </div>
              <button onClick={reset} style={s.secondaryBtn}>Screen Another List</button>
            </div>
            <div style={s.statRow}>
              <StatPill label="Screened" value={results.length} color="#1d6fdb" />
              <StatPill label="Kids Y/N = Yes" value={kidsYes} color="#16a34a" />
              <StatPill label="High income" value={highIncome} color="#7c3aed" />
            </div>
            <p style={s.sectionLabel}>Top industries</p>
            <div style={s.industryRow}>
              {topIndustries.map(([industry, count]) => <span key={industry} style={s.industryPill}>{industry}: {count}</span>)}
            </div>
          </div>
          <div style={s.logScroll}>
            <table style={s.logTable}>
              <thead>
                <tr>{['Name', '#', 'Estimated Income', 'Industry', 'Kids Y/N', 'Confidence'].map(h => <th key={h} style={s.th}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {results.map(row => (
                  <tr key={row.custNo}>
                    <td style={s.td}><strong>{row.name}</strong></td>
                    <td style={s.td}>#{row.custNo}</td>
                    <td style={s.td}>{row.income}</td>
                    <td style={s.td}>{row.industry}</td>
                    <td style={s.td}><YesNoPill value={row.kids} /></td>
                    <td style={s.td}>{row.confidence}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  )
}

function DemographicScreening() {
  return (
    <div>
      <div style={s.header}>
        <h1 style={s.title}>Demographic Screening</h1>
        <p style={s.subtitle}>Estimate household income, industry, and whether kids are present for a full Tessitura list.</p>
      </div>
      <div style={s.panel}>
        <BulkScreeningTab />
      </div>
    </div>
  )
}

const s = {
  header: { marginBottom: '24px' },
  title: { fontSize: '24px', fontWeight: '700', color: '#0c1a33', marginBottom: '4px', fontFamily: "'Space Grotesk', sans-serif" },
  subtitle: { fontSize: '13px', color: '#4b5563', fontFamily: "'Inter', sans-serif", lineHeight: '1.6', maxWidth: '640px' },
  panel: { backgroundColor: 'white', borderRadius: '12px', padding: '28px', boxShadow: '0 2px 16px rgba(29,111,219,0.08)', border: '1px solid rgba(29,111,219,0.1)', maxWidth: '960px' },
  panelIntro: { marginBottom: '10px' },
  sectionHeading: { fontSize: '16px', fontWeight: '700', color: '#0c1a33', marginBottom: '6px', fontFamily: "'Space Grotesk', sans-serif" },
  sectionBody: { fontSize: '13px', color: '#4b5563', lineHeight: '1.6', marginBottom: '12px', maxWidth: '640px', fontFamily: "'Inter', sans-serif" },
  formGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '14px', alignItems: 'end', marginBottom: '18px', maxWidth: '960px' },
  fieldGroup: { display: 'flex', flexDirection: 'column', gap: '7px', minWidth: 0 },
  fieldLabel: { display: 'block', fontSize: '11px', fontWeight: '800', color: '#7b8794', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 0, fontFamily: "'Inter', sans-serif" },
  input: { width: '100%', boxSizing: 'border-box', height: '42px', padding: '10px 12px', fontSize: '14px', borderRadius: '8px', border: '1px solid rgba(29,111,219,0.18)', backgroundColor: '#f8fafd', color: '#0c1a33', outline: 'none', fontFamily: "'Inter', sans-serif" },
  select: { width: '100%', boxSizing: 'border-box', height: '42px', padding: '10px 12px', fontSize: '14px', borderRadius: '8px', border: '1px solid rgba(29,111,219,0.18)', backgroundColor: '#f8fafd', color: '#0c1a33', outline: 'none', fontFamily: "'Inter', sans-serif" },
  btn: { boxSizing: 'border-box', minHeight: '42px', padding: '10px 18px', background: 'linear-gradient(135deg, #1d6fdb, #38bdf8)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '700', fontFamily: "'Inter', sans-serif", cursor: 'pointer', boxShadow: '0 2px 12px rgba(29,111,219,0.22)', whiteSpace: 'nowrap' },
  formAction: { width: '100%' },
  secondaryBtn: { minHeight: '42px', padding: '10px 18px', background: 'white', color: '#0c1a33', border: '1px solid #d0d4db', borderRadius: '8px', fontSize: '14px', fontWeight: '600', fontFamily: "'Inter', sans-serif", cursor: 'pointer' },
  buttonRow: { display: 'flex', gap: '12px', marginTop: '16px', flexWrap: 'wrap' },
  progressBar: { height: '10px', width: '100%', backgroundColor: '#edf0f5', borderRadius: '6px', overflow: 'hidden', marginTop: '12px' },
  progressFill: { height: '100%', background: 'linear-gradient(90deg, #1d6fdb, #38bdf8)', transition: 'width 120ms linear' },
  previewBox: { marginTop: '24px', padding: '20px', backgroundColor: 'white', borderRadius: '10px', border: '1px solid rgba(29,111,219,0.12)', boxShadow: '0 1px 4px rgba(0,0,0,0.04)', maxWidth: '960px' },
  summaryHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' },
  summarySub: { fontSize: '12px', color: '#9ca3af', margin: '2px 0 0', fontFamily: "'Inter', sans-serif" },
  statRow: { display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '16px' },
  statPill: { padding: '10px 16px', borderRadius: '8px', border: '1.5px solid', backgroundColor: 'white', minWidth: '90px', textAlign: 'center' },
  statValue: { fontSize: '22px', fontWeight: '700', lineHeight: 1, fontFamily: "'Space Grotesk', sans-serif" },
  statLabel: { fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.4px', marginTop: '4px', fontFamily: "'Inter', sans-serif" },
  sectionLabel: { fontSize: '11px', fontWeight: '700', letterSpacing: '0.06em', textTransform: 'uppercase', color: '#9ca3af', marginBottom: '8px', fontFamily: "'Inter', sans-serif" },
  industryRow: { display: 'flex', gap: '6px', flexWrap: 'wrap' },
  industryPill: { background: 'rgba(29,111,219,0.07)', border: '1px solid rgba(29,111,219,0.15)', borderRadius: '100px', padding: '4px 12px', fontSize: '12px', color: '#1d6fdb', fontFamily: "'Inter', sans-serif", fontWeight: '500' },
  logScroll: { maxWidth: '960px', overflow: 'hidden', border: '1px solid rgba(29,111,219,0.12)', borderRadius: '8px', backgroundColor: 'white', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' },
  logTable: { width: '100%', borderCollapse: 'collapse', fontSize: '12px', tableLayout: 'fixed' },
  th: { textAlign: 'left', padding: '8px 12px', background: '#f0f7ff', fontSize: '11px', fontWeight: '700', color: '#4b5563', textTransform: 'uppercase', letterSpacing: '0.4px', fontFamily: "'Inter', sans-serif", borderBottom: '1px solid rgba(29,111,219,0.15)' },
  td: { padding: '8px 12px', verticalAlign: 'middle', fontSize: '13px', color: '#374151', fontFamily: "'Inter', sans-serif", borderBottom: '1px solid #f2f4f7' },
  pill: { display: 'inline-block', borderRadius: '100px', padding: '3px 10px', fontSize: '12px', fontWeight: '700', fontFamily: "'Inter', sans-serif" },
}

export default DemographicScreening
